import React, { useEffect, useState } from "react";
import { Helmet } from 'react-helmet';
import axios from "axios";
import { buildApiUrl } from "../utils/api";

const initialEditForm = {
    name: "", website: "", locations: "", totalArea: "",
    services: "", products: "", description: "", summary: ""
};

function WarehouseList() {
    const [warehouses, setWarehouses] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [toast, setToast] = useState("");
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState(initialEditForm);
    const [editLoading, setEditLoading] = useState(false);
    const itemsPerPage = 10;

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(""), 3500);
    };

    const fetchData = async () => {
        try {
            const response = await axios.get(buildApiUrl("/api/warehouses"));
            setWarehouses(response.data);
            setFiltered(response.data);
        } catch (error) {
            console.error('Error fetching warehouses:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const q = search.toLowerCase().trim();
        if (!q) {
            setFiltered(warehouses);
        } else {
            setFiltered(warehouses.filter(w =>
                (w.name || "").toLowerCase().includes(q) ||
                (w.locations || "").toLowerCase().includes(q) ||
                (w.services || "").toLowerCase().includes(q)
            ));
        }
        setCurrentPage(1);
    }, [search, warehouses]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filtered.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    const handleStatusChange = async (warehouseId, newStatus) => {
        try {
            await axios.put(buildApiUrl(`/api/warehouses/updateFulfillmentStatus/${warehouseId}`), { status: newStatus });
            setWarehouses(prev => prev.map(w => w._id === warehouseId ? { ...w, status: newStatus } : w));
            showToast("Fulfillment status updated.");
        } catch (error) {
            console.error('Error updating status', error);
            showToast("Failed to update status. Please try again.");
        }
    };

    const openEdit = (warehouse) => {
        setEditTarget(warehouse._id);
        setEditForm({
            name: warehouse.name || "",
            website: warehouse.website || "",
            locations: warehouse.locations || "",
            totalArea: warehouse.totalArea || "",
            services: warehouse.services || "",
            products: warehouse.products || "",
            description: warehouse.description || "",
            summary: warehouse.summary || ""
        });
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        try {
            await axios.put(buildApiUrl(`/api/warehouses/${editTarget}`), editForm);
            setWarehouses(prev => prev.map(w => w._id === editTarget ? { ...w, ...editForm } : w));
            setEditTarget(null);
            showToast("Warehouse details updated successfully.");
        } catch (error) {
            console.error('Error updating warehouse', error);
            showToast("Failed to update warehouse. Please try again.");
        } finally {
            setEditLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Warehouse List | Admin</title>
            </Helmet>

            {toast && (
                <div className="admin-toast">{toast}</div>
            )}

            <div className="listHead">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h1 className="mb-0">Warehouse List <span className="badge bg-secondary fs-6">{filtered.length}</span></h1>
                    <input
                        type="text"
                        className="form-control"
                        style={{ maxWidth: "300px" }}
                        placeholder="Search by name, location, service..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                <div className="table-responsive">
                    <table className="table table-bordered table-striped table-hover">
                        <thead className="custom-thead">
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Location(s)</th>
                                <th>Area</th>
                                <th>Services</th>
                                <th>Website</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length > 0 ? (
                                currentItems.map((warehouse, index) => (
                                    <tr key={warehouse._id}>
                                        <td>{indexOfFirstItem + index + 1}</td>
                                        <td>{warehouse.name}</td>
                                        <td>{warehouse.locations}</td>
                                        <td>{warehouse.totalArea}</td>
                                        <td>{warehouse.services}</td>
                                        <td>
                                            <a href={warehouse.website} target="_blank" rel="noreferrer" className="text-truncate d-block" style={{ maxWidth: "120px" }}>
                                                {warehouse.website}
                                            </a>
                                        </td>
                                        <td>
                                            <select
                                                className="form-select form-select-sm"
                                                value={warehouse.status || ""}
                                                onChange={(e) => handleStatusChange(warehouse._id, e.target.value)}
                                            >
                                                <option value="" disabled>Select status</option>
                                                <option value="Currently Fulfillable">Currently Fulfillable</option>
                                                <option value="Not Fulfillable">Not Fulfillable</option>
                                            </select>
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => openEdit(warehouse)}
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="text-center text-muted">No warehouses found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <nav className="d-flex justify-content-center">
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>&laquo;</button>
                            </li>
                            {Array.from({ length: totalPages }, (_, i) => (
                                <li className={`page-item ${currentPage === i + 1 ? 'active' : ''}`} key={i}>
                                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>&raquo;</button>
                            </li>
                        </ul>
                    </nav>
                )}
            </div>

            {/* Edit Modal */}
            {editTarget && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Warehouse</h5>
                                <button className="btn-close" onClick={() => setEditTarget(null)} />
                            </div>
                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Warehouse Name</label>
                                            <input className="form-control" name="name" value={editForm.name} onChange={handleEditChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Website</label>
                                            <input className="form-control" name="website" value={editForm.website} onChange={handleEditChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Locations <small className="text-muted">(comma-separated)</small></label>
                                            <input className="form-control" name="locations" value={editForm.locations} onChange={handleEditChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Total Area (sq.ft)</label>
                                            <input className="form-control" name="totalArea" value={editForm.totalArea} onChange={handleEditChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Services <small className="text-muted">(comma-separated)</small></label>
                                            <input className="form-control" name="services" value={editForm.services} onChange={handleEditChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Products <small className="text-muted">(comma-separated)</small></label>
                                            <input className="form-control" name="products" value={editForm.products} onChange={handleEditChange} required />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label">Description</label>
                                            <textarea className="form-control" rows="3" name="description" value={editForm.description} onChange={handleEditChange} required />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label">Summary</label>
                                            <textarea className="form-control" rows="4" name="summary" value={editForm.summary} onChange={handleEditChange} required />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setEditTarget(null)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={editLoading}>
                                        {editLoading ? "Saving..." : "Save Changes"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default WarehouseList;
